/**
 * KillSwitchSection — DevTools sekcja „Awaryjne odzyskiwanie workflow".
 *
 * Zakres (Round 2026-02, Global Workflow Recovery):
 *   1. Karta ustawień globalnego progu klasyfikacji „stale" (min + sekundy,
 *      backend trzyma int w sekundach).
 *   2. Tabela globalna wszystkich aktywnych workflow (cross-tenant).
 *   3. Per-row przycisk „Zakończ workflow" — konkretny `job_id` z modalem
 *      Confirm (bez wpisywania KILL).
 *
 * Endpointy backend:
 *   - GET  /api/admin/settings/workflow-recovery
 *   - PUT  /api/admin/settings/workflow-recovery
 *   - GET  /api/dev-tools/payment-packages/workflow-status-global
 *   - POST /api/dev-tools/payment-packages/terminate-workflow
 *
 * Audyt: /app/docs/GLOBAL_WORKFLOW_RECOVERY_SETTINGS_AUDIT.md
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  AlertTriangle, RefreshCw, Loader2, ShieldAlert, Skull, CheckCircle2,
  Activity, Clock, Save, Settings,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Formatowanie sekund → „12 min 34 s" (bez zaokrągleń). */
const fmtDuration = (s) => {
  if (s == null) return "—";
  const sec = Math.max(0, Math.floor(s));
  const m = Math.floor(sec / 60);
  const rest = sec % 60;
  if (m === 0) return `${rest} s`;
  if (rest === 0) return `${m} min`;
  return `${m} min ${rest} s`;
};

/** Badge środowiska: PREVIEW (żółty) / PRODUCTION (czerwony). */
const EnvBadge = ({ environment }) => {
  const isProd = environment === "PRODUCTION";
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide ${
        isProd
          ? "bg-red-100 text-red-700 ring-1 ring-red-300"
          : "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
      }`}
      data-testid="dev-kill-env-badge"
    >
      {isProd ? <ShieldAlert className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
      {environment || "?"}
    </span>
  );
};

// ── Sekcja: Globalne ustawienie progu ──────────────────────────────────
function ThresholdSettingsCard({ onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/settings/workflow-recovery`,
        { withCredentials: true },
      );
      setSettings(r.data || null);
      setMinutes(Number(r.data?.minutes ?? 0));
      setSeconds(Number(r.data?.seconds ?? 0));
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Wymagane uprawnienia administratora.");
      } else {
        toast.error("Nie udało się pobrać ustawień progu.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const totalSeconds = Math.max(0, Math.floor(Number(minutes) || 0)) * 60
    + Math.max(0, Math.floor(Number(seconds) || 0));

  const validationError = (() => {
    const m = Number(minutes);
    const s = Number(seconds);
    if (!Number.isInteger(m) || m < 0) return "Minuty: liczba całkowita ≥ 0.";
    if (!Number.isInteger(s) || s < 0 || s > 59) return "Sekundy: liczba całkowita 0–59.";
    if (totalSeconds < 1) return "Próg musi wynosić co najmniej 1 sekundę.";
    if (settings?.max_seconds && totalSeconds > settings.max_seconds) {
      return `Próg nie może przekraczać ${settings.max_seconds} s.`;
    }
    return null;
  })();

  const handleSave = async () => {
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(
        `${API}/admin/settings/workflow-recovery`,
        { workflow_stale_threshold_seconds: totalSeconds },
        { withCredentials: true },
      );
      setSettings(r.data);
      setMinutes(Number(r.data?.minutes ?? 0));
      setSeconds(Number(r.data?.seconds ?? 0));
      toast.success(`Zapisano próg: ${fmtDuration(r.data?.workflow_stale_threshold_seconds)}.`);
      if (onSaved) onSaved(r.data?.workflow_stale_threshold_seconds);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || "Nie udało się zapisać progu.";
      toast.error(typeof msg === "string" ? msg : "Nie udało się zapisać progu.");
    } finally {
      setSaving(false);
    }
  };

  if (loading && !settings) {
    return (
      <Card>
        <CardContent className="p-5 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Wczytywanie ustawień…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="dev-kill-threshold-settings">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-slate-100 shrink-0">
            <Settings className="w-4 h-4 text-slate-700" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-gray-900">
              Globalny próg wykrywania zawieszenia
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Ustawienie decyduje kiedy workflow oznaczamy jako <b>stale</b> w globalnym panelu.
              Nie uruchamia recovery ani nie kończy workflow.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600" htmlFor="threshold-minutes">Minuty (≥ 0)</label>
            <Input
              id="threshold-minutes"
              type="number"
              inputMode="numeric"
              min={0}
              step={1}
              value={minutes}
              onChange={(e) => setMinutes(e.target.value === "" ? 0 : parseInt(e.target.value, 10))}
              data-testid="dev-kill-threshold-minutes-input"
            />
          </div>
          <div>
            <label className="text-xs text-gray-600" htmlFor="threshold-seconds">Sekundy (0–59)</label>
            <Input
              id="threshold-seconds"
              type="number"
              inputMode="numeric"
              min={0}
              max={59}
              step={1}
              value={seconds}
              onChange={(e) => setSeconds(e.target.value === "" ? 0 : parseInt(e.target.value, 10))}
              data-testid="dev-kill-threshold-seconds-input"
            />
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <div className="text-gray-600" data-testid="dev-kill-threshold-computed">
            Łącznie: <b className="font-mono">{totalSeconds}</b> s
            <span className="text-gray-400"> · {fmtDuration(totalSeconds)}</span>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving || Boolean(validationError)}
            data-testid="dev-kill-threshold-save-btn"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Zapisywanie…</>
            ) : (
              <><Save className="w-4 h-4 mr-1.5" /> Zapisz próg</>
            )}
          </Button>
        </div>
        {validationError && (
          <p className="text-xs text-red-600" data-testid="dev-kill-threshold-error">
            {validationError}
          </p>
        )}

        {settings && (
          <div className="text-xs text-gray-500 space-y-0.5" data-testid="dev-kill-threshold-meta">
            <div>Aktualny próg w bazie: <span className="font-mono">{settings.workflow_stale_threshold_seconds} s</span> ({fmtDuration(settings.workflow_stale_threshold_seconds)})</div>
            <div>Wartość domyślna: <span className="font-mono">{settings.default_seconds} s</span> ({fmtDuration(settings.default_seconds)}){settings.is_default ? " — nadal używane (brak zapisu w bazie)" : ""}</div>
            {settings.updated_at && (
              <div>Ostatnia zmiana: <span className="font-mono">{settings.updated_at}</span>{settings.updated_by ? ` — ${settings.updated_by}` : ""}</div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


// ── Sekcja: Globalna lista workflow ────────────────────────────────────
function WorkflowListCard({ refreshTick }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null); // {job_id, run_key, user_id, kind}
  const [terminating, setTerminating] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/dev-tools/payment-packages/workflow-status-global`,
        { withCredentials: true },
      );
      setData(r.data || null);
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Wymagane uprawnienia administratora.");
      } else {
        toast.error("Nie udało się pobrać globalnego statusu workflow.");
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus, refreshTick]);

  const handleConfirmTerminate = async () => {
    if (!confirmTarget) return;
    setTerminating(true);
    try {
      const body = {};
      if (confirmTarget.job_id) body.job_id = confirmTarget.job_id;
      else if (confirmTarget.run_key) body.run_key = confirmTarget.run_key;
      const r = await axios.post(
        `${API}/dev-tools/payment-packages/terminate-workflow`,
        body,
        { withCredentials: true },
      );
      const d = r.data || {};
      const diagId = d.diagnostic_id ? ` (id: ${d.diagnostic_id})` : "";
      if (d.status === "already_terminal") {
        toast.info(`Workflow był już w stanie terminalnym.${diagId}`);
      } else if (d.status === "partial_cleanup") {
        toast.warning(`Workflow zakończony częściowo — sprawdź identyfikator diagnostyczny.${diagId}`);
      } else {
        toast.success(`Workflow został zakończony.${diagId}`);
      }
      setConfirmTarget(null);
      await fetchStatus();
      try { window.dispatchEvent(new CustomEvent("pax:packages:refresh")); } catch { /* noop */ }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 404) {
        toast.error("Nie znaleziono workflow o podanym identyfikatorze.");
      } else if (status === 403) {
        toast.error("Wymagane uprawnienia administratora.");
      } else {
        const msg = e?.response?.data?.detail?.message
          || e?.response?.data?.detail
          || "Błąd zakończenia workflow.";
        toast.error(typeof msg === "string" ? msg : "Błąd zakończenia workflow.");
      }
    } finally {
      setTerminating(false);
    }
  };

  if (loading && !data) {
    return (
      <Card>
        <CardContent className="p-5 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Wczytywanie workflow…
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <CardContent className="p-5">
          <p className="text-sm text-gray-600" data-testid="dev-kill-empty">
            Nie udało się pobrać globalnego statusu workflow. Sprawdź czy jesteś zalogowany jako administrator.
          </p>
          <Button variant="outline" size="sm" className="mt-3" onClick={fetchStatus}>
            <RefreshCw className="w-4 h-4 mr-1.5" /> Odśwież
          </Button>
        </CardContent>
      </Card>
    );
  }

  const rows = data.workflows || [];
  const totalStale = data.stale_count ?? 0;
  const threshold = data.workflow_stale_threshold_seconds ?? 0;

  return (
    <>
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                Aktywne workflow (globalnie)
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Cross-tenant lista wszystkich aktywnych workflow Paczek Płatności.
                Próg <b>stale</b>: <span className="font-mono">{threshold} s</span>
                <span className="text-gray-400"> · {fmtDuration(threshold)}</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              {totalStale > 0 ? (
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700 ring-1 ring-red-200"
                  data-testid="dev-kill-badge-stale-count"
                >
                  <Activity className="w-3 h-3" /> {totalStale} stale
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-700 ring-1 ring-green-200"
                  data-testid="dev-kill-badge-no-stale"
                >
                  <CheckCircle2 className="w-3 h-3" /> Brak stale
                </span>
              )}
              <Button variant="outline" size="sm" onClick={fetchStatus} data-testid="dev-kill-refresh-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="text-sm text-gray-500 italic" data-testid="dev-kill-no-active">
              Brak aktywnych workflow.
            </p>
          ) : (
            <div className="overflow-x-auto" data-testid="dev-kill-workflows-table">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase tracking-wide border-b border-gray-200">
                    <th className="text-left py-2 pr-3">User</th>
                    <th className="text-left py-2 pr-3">Typ</th>
                    <th className="text-left py-2 pr-3">Job / Run</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2 pr-3">Postęp</th>
                    <th className="text-left py-2 pr-3">Od heartbeat</th>
                    <th className="text-right py-2 pl-3">Akcja</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((w, i) => {
                    const rowKey = w.job_id || w.run_key || `row_${i}`;
                    const isStale = Boolean(w.is_stale);
                    return (
                      <tr
                        key={rowKey}
                        className={`border-b border-gray-100 last:border-0 ${isStale ? "bg-red-50/50" : ""}`}
                        data-testid={`dev-kill-workflow-row-${rowKey}`}
                      >
                        <td className="py-2 pr-3 font-mono text-xs text-gray-700 break-all" data-testid={`dev-kill-row-user-${rowKey}`}>
                          {w.user_id || "—"}
                        </td>
                        <td className="py-2 pr-3 text-xs text-gray-600">{w.kind}</td>
                        <td className="py-2 pr-3 font-mono text-xs text-gray-700 break-all">
                          {w.job_id ? (<div>job: {w.job_id}</div>) : null}
                          {w.run_key ? (<div className="text-gray-500">run: {w.run_key}</div>) : null}
                        </td>
                        <td className="py-2 pr-3">
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-800">
                            {w.status || "—"}
                          </span>
                          {isStale && (
                            <span
                              className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
                              data-testid={`dev-kill-row-stale-${rowKey}`}
                            >
                              <Clock className="w-3 h-3" /> stale
                            </span>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs text-gray-700">
                          {w.total_count > 0 ? `${w.done_count}/${w.total_count}` : "—"}
                          {w.current_vendor_nip ? (
                            <div className="text-[11px] text-gray-500 font-mono">NIP: {w.current_vendor_nip}</div>
                          ) : null}
                        </td>
                        <td className="py-2 pr-3 text-xs text-gray-700" data-testid={`dev-kill-row-heartbeat-${rowKey}`}>
                          {fmtDuration(w.seconds_since_last_update)}
                          <div className="text-[11px] text-gray-400 font-mono">{w.last_heartbeat_at || "—"}</div>
                        </td>
                        <td className="py-2 pl-3 text-right">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setConfirmTarget({
                              job_id: w.job_id || null,
                              run_key: w.run_key || null,
                              user_id: w.user_id || null,
                              kind: w.kind,
                            })}
                            data-testid={`dev-kill-terminate-btn-${rowKey}`}
                          >
                            <Skull className="w-3.5 h-3.5 mr-1" />
                            Zakończ workflow
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm modal */}
      <AlertDialog open={Boolean(confirmTarget)} onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}>
        <AlertDialogContent data-testid="dev-kill-terminate-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Skull className="w-5 h-5 text-red-600" />
              Zakończyć workflow?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Ta operacja zakończy wskazany workflow, oznaczy job / run jako
              <code className="mx-1 px-1 bg-slate-100 rounded font-mono text-xs">killed_by_admin</code>
              i zwolni powiązane blokady. Poprawnie zapisane paczki i faktury pozostaną nietknięte.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {confirmTarget && (
            <div className="bg-gray-50 rounded-md p-3 space-y-1 text-xs" data-testid="dev-kill-terminate-modal-target">
              <div><span className="text-gray-500">User:</span> <span className="font-mono">{confirmTarget.user_id || "—"}</span></div>
              <div><span className="text-gray-500">Typ:</span> {confirmTarget.kind || "—"}</div>
              {confirmTarget.job_id && (<div><span className="text-gray-500">job_id:</span> <span className="font-mono">{confirmTarget.job_id}</span></div>)}
              {confirmTarget.run_key && (<div><span className="text-gray-500">run_key:</span> <span className="font-mono">{confirmTarget.run_key}</span></div>)}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={terminating} data-testid="dev-kill-terminate-cancel-btn">
              Anuluj
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirmTerminate(); }}
              disabled={terminating}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="dev-kill-terminate-confirm-btn"
            >
              {terminating ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Kończenie…</>
              ) : (
                <><Skull className="w-4 h-4 mr-1.5" /> Zakończ workflow</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}


// ── Główny komponent sekcji ────────────────────────────────────────────
export default function KillSwitchSection() {
  const [envInfo, setEnvInfo] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Prosty fetch środowiska (współdzielony z workflow-status-global).
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(
          `${API}/dev-tools/payment-packages/workflow-status-global`,
          { withCredentials: true },
        );
        setEnvInfo({ environment: r.data?.environment });
      } catch {
        setEnvInfo(null);
      }
    })();
  }, [refreshTick]);

  const env = envInfo?.environment;
  const isProd = env === "PRODUCTION";

  return (
    <div className="space-y-4" data-testid="dev-kill-switch-section">
      {/* Nagłówek */}
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-red-50 shrink-0">
          <Skull className="w-5 h-5 text-red-600" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-gray-900">Awaryjne odzyskiwanie workflow</h2>
            {env && <EnvBadge environment={env} />}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Globalny panel administratora. Ustawienie progu i punktowe kończenie zawieszonych procesów.
          </p>
        </div>
      </div>

      {/* Ostrzeżenie środowiskowe */}
      {env && (
        <div
          className={`rounded-lg p-3 text-sm border ${
            isProd
              ? "bg-red-50 border-red-300 text-red-800"
              : "bg-amber-50 border-amber-300 text-amber-800"
          }`}
          data-testid="dev-kill-env-warning"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <p>
              {isProd
                ? "Operacje wykonywane w tym panelu działają na aktywnych workflow produkcyjnych — cross-tenant."
                : "Operacje wykonywane w tym panelu działają na środowisku preview. Nie odblokują workflow produkcyjnych."}
            </p>
          </div>
        </div>
      )}

      <ThresholdSettingsCard onSaved={() => setRefreshTick((t) => t + 1)} />
      <WorkflowListCard refreshTick={refreshTick} />
    </div>
  );
}
